import React, { useState, useEffect, useMemo, useCallback } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Form, InputGroup, Button } from "react-bootstrap";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { hasAccess } from '../../utils/permissionHelper';


// 🔹 Reusable Card Wrapper
function FormCard({ title, children }) {
  return (
    <div
      className="card border-0 shadow-sm rounded-4 mb-4 bg-white"
      style={{ background: "#ffffff" }}
    >
      <div
        className="card-header bg-white fw-semibold fs-6 border-bottom"
        style={{ color: "#3b6ea5" }}
      >
        {title}
      </div>
      <div className="card-body pt-3">{children}</div>
    </div>
  );
}

// 🔹 Quotation Material Table
function QuotationTable() {
  const [rows, setRows] = useState([
    { material: "", description: "", qty: 1, unitPrice: 0, total: 0 },
  ]);

  const addRow = () =>
    setRows([
      ...rows,
      { material: "", description: "", qty: 1, unitPrice: 0, total: 0 },
    ]);

  const handleChange = (i, field, value) => {
    const updated = [...rows];
    updated[i][field] = value;
    if (field === "qty" || field === "unitPrice") {
      updated[i].total =
        (Number(updated[i].qty) || 0) * (Number(updated[i].unitPrice) || 0);
    }
    setRows(updated);
  };

  const deleteRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  return (
    <div className="table-responsive">
      <table className="table align-middle table-bordered">
        <thead className="table-light">
          <tr>
            <th>Sr No</th>
            <th>Material</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Total</th>
            <th style={{ width: "60px" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={r.material}
                  onChange={(e) =>
                    handleChange(i, "material", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={r.description}
                  onChange={(e) =>
                    handleChange(i, "description", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={r.qty}
                  onChange={(e) => handleChange(i, "qty", e.target.value)}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={r.unitPrice}
                  onChange={(e) =>
                    handleChange(i, "unitPrice", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm bg-light"
                  value={r.total}
                  readOnly
                />
              </td>
              <td className="text-center">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteRow(i)}
                >
                  🗑
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={7} className="text-center">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={addRow}
              >
                + Add Material
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// 🔹 Main Component
export default function QuotationCreate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Form state
  const [leadId, setLeadId] = useState("");
  const [title, setTitle] = useState("");
  const [projectTypeId, setProjectTypeId] = useState("");
  const [quotationDate, setQuotationDate] = useState("");
  const [validDate, setValidDate] = useState("");
  const [bomId, setBomId] = useState("");
  const [note, setNote] = useState("");

  // Toggle states for sections
  const [showPlantConfiguration, setShowPlantConfiguration] = useState(true);
  const [plantConfiguration, setPlantConfiguration] = useState("");
  const [showWarranty, setShowWarranty] = useState(true);
  const [warranty, setWarranty] = useState("");
  const [showScope, setShowScope] = useState(true);
  const [scope, setScope] = useState("");
  const [showCommercialTermsAndConditions, setShowCommercialTermsAndConditions] = useState(true);
  const [commercialTermsAndConditions, setCommercialTermsAndConditions] = useState("");
  const [showResidentialTermsAndConditions, setShowResidentialTermsAndConditions] = useState(true);
  const [residentialTermsAndConditions, setResidentialTermsAndConditions] = useState("");
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(true);
  const [privacyPolicy, setPrivacyPolicy] = useState("");

  // API data states
  const [leads, setLeads] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [variants, setVariants] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  // Current logged in user (for permission checks)
  const [currentUser, setCurrentUser] = useState(null);
  const [businessId, setBusinessId] = useState('');
  const [isBomSelected, setIsBomSelected] = useState(false);
  const [bomMarginLocked, setBomMarginLocked] = useState(false);

  // Commercial offer states
  const [particulars, setParticulars] = useState([{ description: "", amount: "" }]);
  const [subsidies, setSubsidies] = useState([{ description: "", amount: "" }]);
  const [paymentTerms, setPaymentTerms] = useState([{ description: "", amount: "", percentage: "" }]);

  // Calculation states
  const [discount, setDiscount] = useState(0);
  const [margin, setMargin] = useState(0);
  const [adjustment, setAdjustment] = useState(0);
  const [gstRate, setGstRate] = useState(0); // Default GST rate
  const [meterCharge, setMeterCharge] = useState(0);
  const [subsidyPercentage, setSubsidyPercentage] = useState(0);

  // Permission flags derived from user role/plan
  const canViewRate = hasAccess(currentUser, 'Rate', 'View');
  const canEditRate = hasAccess(currentUser, 'Rate', 'Edit');
  const canViewTaxRate = hasAccess(currentUser, 'Tax-Rate', 'View');
  const canEditTaxRate = hasAccess(currentUser, 'Tax-Rate', 'Edit');
  const canViewTaxAmount = hasAccess(currentUser, 'Tax-Amount', 'View');
  const canViewTotal = hasAccess(currentUser, 'Total', 'View');
  const canEditTotal = hasAccess(currentUser, 'Total', 'Edit');

  const handleAddLead = () => {
    navigate('/user/lead/create');
  };

  // Get business ID from localStorage
  useEffect(() => {
    const storedBusinessId = localStorage.getItem('businessId');
    if (storedBusinessId) {
      setBusinessId(storedBusinessId);
    }
    // Load current user for permission checks
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (err) {
      console.warn('Failed to parse stored user for permissions', err);
    }
  }, []);

  // API fetch functions
  const fetchLeads = async () => {
    try {
      let url = '/admin/api/lead';
      if (businessId) {
        url += `?business=${businessId}`;
      }
      const response = await axiosInstance.get(url);
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toastError('Failed to fetch leads');
    }
  };

  const fetchProjectTypes = async () => {
    try {
      let url = '/admin/api/project-type';
      if (businessId) {
        url += `?business=${businessId}`;
      }
      const response = await axiosInstance.get(url);
      setProjectTypes(response.data.projectTypes || []);
    } catch (error) {
      console.error('Error fetching project types:', error);
      toastError('Failed to fetch project types');
    }
  };

  const fetchBoms = async () => {
    try {
      let url = '/api/bom';
      if (businessId) {
        url += `?business=${businessId}`;
      }
      const response = await axiosInstance.get(url);
      setBoms(response.data.boms || []);
    } catch (error) {
      console.error('Error fetching BOMs:', error);
      toastError('Failed to fetch BOMs');
    }
  };

  const fetchItems = async () => {
    try {
      let url = '/api/item';
      if (businessId) {
        url += `?business=${businessId}`;
      }
      const response = await axiosInstance.get(url);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      toastError('Failed to fetch items');
    }
  };

  const fetchQuotationSettings = async () => {
    try {
      const response = await axiosInstance.get('/admin/api/quatation-setting');
      if (response.data.Status === 1) {
        const settings = response.data.settings || response.data.data;
        if (settings) {
          // Apply settings to form fields
          setShowPlantConfiguration(settings.showplantconfiguraton !== undefined ? settings.showplantconfiguraton : true);
          setPlantConfiguration(settings.plantconfiguraton || "");
          setShowWarranty(settings.showWarranty !== undefined ? settings.showWarranty : true);
          setWarranty(settings.warranty || "");
          setShowScope(settings.showScope !== undefined ? settings.showScope : true);
          setScope(settings.scope || "");
          setShowCommercialTermsAndConditions(settings.showCommercialTermsAndConditions !== undefined ? settings.showCommercialTermsAndConditions : true);
          setCommercialTermsAndConditions(settings.commercialTermsAndConditions || "");
          setShowResidentialTermsAndConditions(settings.showResidentialTermsAndConditions !== undefined ? settings.showResidentialTermsAndConditions : true);
          setResidentialTermsAndConditions(settings.residentialTermsAndConditions || "");
          setShowPrivacyPolicy(settings.showPrivacyPolicy !== undefined ? settings.showPrivacyPolicy : true);
          setPrivacyPolicy(settings.privacyPolicy || "");
        }
        console.log('Quotation settings loaded successfully');
      } else {
        console.warn('Failed to fetch quotation settings');
      }
    } catch (error) {
      console.error('Error fetching quotation settings:', error);
      // Don't show error toast as settings are optional
    }
  };

  const saveQuotationSettings = async () => {
    try {
      const currentBusinessId = localStorage.getItem('businessId');
      if (!currentBusinessId) {
        return;
      }

      const settingsData = {
        business: currentBusinessId,
        showplantconfiguraton: showPlantConfiguration,
        plantconfiguraton: plantConfiguration,
        showWarranty,
        warranty,
        showScope,
        scope,
        showCommercialTermsAndConditions,
        commercialTermsAndConditions,
        showResidentialTermsAndConditions,
        residentialTermsAndConditions,
        showPrivacyPolicy,
        privacyPolicy
      };

      const response = await axiosInstance.post('/admin/api/quatation-setting', settingsData);
      if (response.data.Status === 1) {
        console.log('Quotation settings saved successfully');
        // Optional: Show a subtle success indicator
        // toastSuccess('Settings saved automatically', { autoHideDuration: 2000 });
      }
    } catch (error) {
      console.error('Error saving quotation settings:', error);
      // Don't show error toast to avoid interrupting user flow
      // Could add a subtle warning indicator here if needed
    }
  };

  // Debounced auto-save for settings
  const debouncedSaveSettings = useCallback(
    debounce(() => {
      saveQuotationSettings();
    }, 2000), // Save 2 seconds after user stops typing
    [showPlantConfiguration, plantConfiguration, showWarranty, warranty, showScope, scope,
      showCommercialTermsAndConditions, commercialTermsAndConditions,
      showResidentialTermsAndConditions, residentialTermsAndConditions,
      showPrivacyPolicy, privacyPolicy]
  );

  // Auto-save settings when they change
  useEffect(() => {
    if (businessId) {
      debouncedSaveSettings();
    }
  }, [showPlantConfiguration, plantConfiguration, showWarranty, warranty, showScope, scope,
    showCommercialTermsAndConditions, commercialTermsAndConditions,
    showResidentialTermsAndConditions, residentialTermsAndConditions,
    showPrivacyPolicy, privacyPolicy, debouncedSaveSettings, businessId]);

  // Simple debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const fetchBomItems = async (bomId) => {
    try {
      setLoading(true);
      // Fetch both BOM items and all items
      const [bomResponse, itemsResponse] = await Promise.all([
        axiosInstance.get(`/api/bom/${bomId}`),
        axiosInstance.get(`/api/item${businessId ? `?businessId=${businessId}` : ''}`)
      ]);

      // Update items list
      setItems(itemsResponse.data.items || []);

      if (bomResponse.data.Status === 1 && bomResponse.data.bom) {
        const bomData = bomResponse.data.bom;
        if (bomData.items && bomData.items.length > 0) {
          // Map BOM items to table rows
          const bomRows = bomData.items.map((bomItem) => {
            const qty = Number(bomItem.qty ?? bomItem.quantity ?? 1) || 0;
            const rate = Number(bomItem.variant?.rate ?? 0) || 0;
            const gst = Number(bomItem.variant?.gst ?? 0) || 0;
            const lineSubtotal = qty * rate;
            const taxAmount = (lineSubtotal * gst) / 100;
            const lineTotal = lineSubtotal + taxAmount;

            return {
              itemName: bomItem.item?._id || bomItem.item,
              variant: bomItem.variant?._id || bomItem.variant,
              companyName: bomItem.companyName || bomItem.variant?.companyName || '',
              unit: bomItem.variant?.unit?._id || bomItem.variant?.unit || '',
              quantity: qty,
              rate: rate,
              taxRate: gst,
              taxAmount: taxAmount.toFixed(2),
              total: lineTotal.toFixed(2),
              description: bomItem.variant?.description || '',
              isBomItem: true // Flag to identify BOM items
            };
          });
          setRows(bomRows);

          // If BOM defines a margin, apply it and lock margin editing
          if (bomData.margin !== undefined && bomData.margin !== null) {
            setMargin(Number(bomData.margin) || 0);
            setBomMarginLocked(true);
          } else {
            setBomMarginLocked(false);
          }

          setIsBomSelected(true);
        } else {
          setRows([{ itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "", taxRate: "", taxAmount: "", total: "", description: "", isBomItem: false }]);
          setIsBomSelected(false);
        }
      }
    } catch (error) {
      console.error('Error fetching BOM items:', error);
      toastError('Failed to fetch BOM items');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotationData = async (quotationId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/quatation/${quotationId}`);
      const quotation = response.data.quatation;

      // Populate form with existing data
      setTitle(quotation.title || "");
      setLeadId(quotation.lead?._id || quotation.lead || "");
      setProjectTypeId(quotation.projectType?._id || quotation.projectType || "");
      setBomId(quotation.bom?._id || quotation.bom || "");
      setNote(quotation.note || "");
      setQuotationDate(quotation.quotationDate ? quotation.quotationDate.split('T')[0] : "");
      setValidDate(quotation.validDate ? quotation.validDate.split('T')[0] : "");

      // Set toggle states
      setShowPlantConfiguration(quotation.showplantconfiguraton || false);
      setPlantConfiguration(quotation.plantconfiguraton || "");
      setShowWarranty(quotation.showWarranty || false);
      setWarranty(quotation.warranty || "");
      setShowScope(quotation.showScope || false);
      setScope(quotation.scope || "");
      setShowCommercialTermsAndConditions(quotation.showCommercialTermsAndConditions || false);
      setCommercialTermsAndConditions(quotation.commercialTermsAndConditions || "");
      setShowResidentialTermsAndConditions(quotation.showResidentialTermsAndConditions || false);
      setResidentialTermsAndConditions(quotation.residentialTermsAndConditions || "");
      setShowPrivacyPolicy(quotation.showPrivacyPolicy || false);
      setPrivacyPolicy(quotation.privacyPolicy || "");

      // Set items
      if (quotation.items && quotation.items.length > 0) {
        const mappedItems = quotation.items.map(item => {
          const quantity = Number(item.qty) || 0;
          const rate = Number(item.variant?.rate) || 0;
          const taxRate = Number(item.variant?.gst) || 0;

          // Calculate subtotal (before tax)
          const subtotal = quantity * rate;
          // Calculate tax amount
          const taxAmount = (subtotal * taxRate) / 100;
          // Calculate total (subtotal + tax)
          const total = subtotal + taxAmount;

          return {
            itemName: item.baseItem || "",
            variant: item.variant?._id || "",
            companyName: item.variant?.companyName || "",
            description: item.variant?.description || "",
            unit: item.variant?.unit?._id || "",
            quantity: quantity,
            rate: rate,
            taxRate: taxRate,
            taxAmount: taxAmount.toFixed(2),
            total: total.toFixed(2)
          };
        });
        setRows(mappedItems);
      }

      // If quotation references a BOM, mark BOM as selected so UI reflects BOM-based behavior
      if (quotation.bom || (quotation.bom && quotation.bom._id)) {
        setIsBomSelected(true);
      } else {
        setIsBomSelected(false);
      }

      // Set commercial offer data
      if (quotation.commercialOffer) {
        const co = quotation.commercialOffer;

        // Handle particulars - check both old and new format
        if (co.particulars && co.particulars.length > 0) {
          // New nested format
          if (co.particulars[0].items) {
            const particularItems = co.particulars[0].items || [];
            setParticulars(particularItems.length > 0 ? particularItems : [{ description: "", amount: "" }]);
          }
          // Old direct format (backward compatibility)
          else if (Array.isArray(co.particulars[0])) {
            setParticulars(co.particulars[0].length > 0 ? co.particulars[0] : [{ description: "", amount: "" }]);
          }
          // Direct items format
          else {
            setParticulars([{ description: "", amount: "" }]);
          }
        } else {
          setParticulars([{ description: "", amount: "" }]);
        }

        // Handle subsidies - check both old and new format
        if (co.subsidies && co.subsidies.length > 0) {
          // New nested format
          if (co.subsidies[0].items) {
            const subsidyItems = co.subsidies[0].items || [];
            setSubsidies(subsidyItems.length > 0 ? subsidyItems : [{ description: "", amount: "" }]);
          }
          // Old direct format (backward compatibility)
          else if (Array.isArray(co.subsidies[0])) {
            setSubsidies(co.subsidies[0].length > 0 ? co.subsidies[0] : [{ description: "", amount: "" }]);
          }
          // Direct items format
          else {
            setSubsidies([{ description: "", amount: "" }]);
          }
        } else {
          setSubsidies([{ description: "", amount: "" }]);
        }

        // Handle payment terms - check both old and new format
        if (co.paymentTerms && co.paymentTerms.length > 0) {
          // New nested format
          if (co.paymentTerms[0].items) {
            const paymentItems = co.paymentTerms[0].items || [];
            setPaymentTerms(paymentItems.length > 0 ? paymentItems : [{ description: "", amount: "", percentage: "" }]);
          }
          // Old direct format (backward compatibility)
          else if (Array.isArray(co.paymentTerms[0])) {
            setPaymentTerms(co.paymentTerms[0].length > 0 ? co.paymentTerms[0] : [{ description: "", amount: "", percentage: "" }]);
          }
          // Direct items format
          else {
            setPaymentTerms([{ description: "", amount: "", percentage: "" }]);
          }
        } else {
          setPaymentTerms([{ description: "", amount: "", percentage: "" }]);
        }
      }

      // Set calculation states if available
      if (quotation.calculations) {
        setDiscount(Number(quotation.calculations.discount) || 0);
        setMargin(Number(quotation.calculations.margin) || 0);
        setAdjustment(Number(quotation.calculations.adjustment) || 0);
        setGstRate(Number(quotation.calculations.gstRate) || 0);
        setMeterCharge(Number(quotation.calculations.meterCharge) || 0);
        setSubsidyPercentage(Number(quotation.calculations.subsidyPercentage) || 0);
      } else {
        // Set from individual fields if calculations object doesn't exist
        setDiscount(Number(quotation.discount) || 0);
        setMargin(Number(quotation.margin) || 0);
        setAdjustment(Number(quotation.adjustment) || 0);
        setGstRate(Number(quotation.gstRate) || 0);
        setMeterCharge(Number(quotation.meterCharge) || 0);
        setSubsidyPercentage(Number(quotation.subsidyPercentage) || 0);
      }

      // If this quotation is based on a BOM and margin was provided, lock the margin input
      if ((quotation.bom || quotation.bom?._id) && (quotation.calculations?.margin || quotation.margin)) {
        setBomMarginLocked(true);
      } else {
        setBomMarginLocked(false);
      }

    } catch (error) {
      console.error('Error fetching quotation:', error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    if (businessId) {
      fetchLeads();
      fetchProjectTypes();
      fetchBoms();
      fetchItems();
      fetchQuotationSettings();
    }
  }, [businessId]);

  useEffect(() => {
    if (isEdit && id) {
      fetchQuotationData(id);
    }
  }, [isEdit, id]);

  // Auto-set valid date to 7 days after quotation date when creating new quotation
  useEffect(() => {
    // Only auto-set when user is creating a new quotation (not editing)
    if (!isEdit && quotationDate) {
      try {
        const selectedDate = new Date(quotationDate);
        selectedDate.setDate(selectedDate.getDate() + 7);
        const validDateStr = selectedDate.toISOString().split('T')[0];
        setValidDate(validDateStr);
      } catch (err) {
        // If parsing fails, do nothing
        console.warn('Failed to auto-calculate valid date from quotationDate:', err);
      }
    }
  }, [quotationDate, isEdit]);

  // =============================
  // 🔹 Material Table State & Functions
  // =============================
  const [rows, setRows] = useState([
    { itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "", taxRate: "", taxAmount: "", total: "", description: "" },
  ]);

  // =============================
  // 🔹 Calculation Functions with useMemo
  // =============================

  // Calculate subtotal (sum of all item totals)
  const subtotal = useMemo(() => {
    return rows.reduce((sum, row) => sum + (Number(row.total) || 0), 0);
  }, [rows]);

  // Calculate margin amount
  const marginAmount = useMemo(() => {
    return (subtotal * (Number(margin) || 0)) / 100;
  }, [subtotal, margin]);

  // Calculate GST amount - recalculates when margin changes
  const gstAmount = useMemo(() => {
    const discountAmount = Number(discount) || 0;

    // GST is calculated on: (subtotal - discount + margin)
    const baseForGst = subtotal - discountAmount + marginAmount;
    return (baseForGst * (Number(gstRate) || 0)) / 100;
  }, [subtotal, discount, marginAmount, gstRate]);

  // Calculate grand total - recalculates whenever any component changes
  const grandTotal = useMemo(() => {
    const discountAmount = Number(discount) || 0;
    const adjustmentAmount = Number(adjustment) || 0;

    // Final calculation: subtotal - discount + margin + gst - adjustment
    return subtotal - discountAmount + marginAmount + gstAmount - adjustmentAmount;
  }, [subtotal, discount, marginAmount, gstAmount, adjustment]);

  // Simple grand total calculation: subtotal - discount
  const simpleGrandTotal = useMemo(() => {
    const discountAmount = Number(discount) || 0;
    let adjustmentAmount = Number(adjustment) || 0;
    return subtotal - discountAmount + gstAmount + marginAmount - adjustmentAmount;
  }, [subtotal, discount, gstAmount, marginAmount, adjustment]);

  // Calculate subsidy amount based on percentage
  const subsidyAmount = useMemo(() => {
    const meterChargeAmount = Number(meterCharge) || 0;
    const totalWithMeter = grandTotal + meterChargeAmount;
    return (totalWithMeter * (Number(subsidyPercentage) || 0)) / 100;
  }, [grandTotal, meterCharge, subsidyPercentage]);

  // Calculate final amount after subsidy
  const finalAmount = useMemo(() => {
    const meterChargeAmount = Number(meterCharge) || 0;
    return grandTotal + meterChargeAmount - subsidyAmount;
  }, [grandTotal, meterCharge, subsidyAmount]);

  // Calculate After Subsidy Cost for payment terms
  const afterSubsidyCost = useMemo(() => {
    const netPayableAmount = simpleGrandTotal + particulars.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const subsidyAmount = subsidies.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    return netPayableAmount - subsidyAmount;
  }, [simpleGrandTotal, particulars, subsidies]);

  // Legacy function wrappers for backward compatibility
  const calculateSubtotal = () => subtotal;
  const calculateMarginAmount = () => marginAmount;
  const calculateGstAmount = () => gstAmount;
  const calculateGrandTotal = () => grandTotal;
  const calculateSimpleGrandTotal = () => simpleGrandTotal;
  const calculateSubsidyAmount = () => subsidyAmount;
  const calculateFinalAmount = () => finalAmount;
  const calculateAfterSubsidyCost = () => afterSubsidyCost;

  // Get variants for selected item
  const getVariantsForItem = (itemId) => {
    const selectedItem = items.find(item => item._id === itemId);
    return selectedItem?.variants || [];
  };

  // Get units from all variants
  const getAllUnits = () => {
    const unitSet = new Set();
    items.forEach(item => {
      item.variants?.forEach(variant => {
        if (variant.unit) {
          unitSet.add(JSON.stringify({ value: variant.unit._id, label: variant.unit.name }));
        }
      });
    });
    return Array.from(unitSet).map(unit => JSON.parse(unit));
  };

  // 🔹 Update Row Field
  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    const currentRow = updatedRows[index];

    // For BOM items, only allow quantity changes
    // For BOM items, allow quantity and rate edits only (others locked)
    if (currentRow.isBomItem && !["quantity", "rate"].includes(field)) {
      toastError('Only quantity and rate can be modified for BOM items');
      return;
    }

    updatedRows[index][field] = value;

    // Auto-populate variant details when item changes (only for manual items)
    if (field === "itemName" && !currentRow.isBomItem) {
      const selectedItem = items.find(item => item._id === value);
      if (selectedItem && selectedItem.variants && selectedItem.variants.length > 0) {
        updatedRows[index].variant = "";
        updatedRows[index].rate = "";
        updatedRows[index].unit = "";
        updatedRows[index].taxRate = "";
      }
    }

    // Auto-populate details when variant changes (only for manual items)
    if (field === "variant" && !currentRow.isBomItem) {
      const selectedItem = items.find(item => item._id === updatedRows[index].itemName);
      if (selectedItem) {
        const selectedVariant = selectedItem.variants.find(variant => variant._id === value);
        if (selectedVariant) {
          updatedRows[index].rate = selectedVariant.rate || "";
          updatedRows[index].unit = selectedVariant.unit?._id || "";
          updatedRows[index].taxRate = selectedVariant.gst || "";
          updatedRows[index].description = selectedVariant.description || "";
          updatedRows[index].companyName = selectedVariant.companyName || "";
          
          // Recalculate totals after variant selection
          const qty = Number(updatedRows[index].quantity) || 0;
          const rate = Number(selectedVariant.rate) || 0;
          const taxRate = Number(selectedVariant.gst) || 0;

          const subtotal = qty * rate;
          const taxAmount = (subtotal * taxRate) / 100;
          const total = subtotal + taxAmount;

          updatedRows[index].taxAmount = taxAmount.toFixed(2);
          updatedRows[index].total = total.toFixed(2);
        }
      }
    }

    // Calculate totals when quantity, rate, or tax rate changes
    if (field === "quantity" || field === "rate" || field === "taxRate") {
      const qty = Number(updatedRows[index].quantity) || 0;
      const rate = Number(updatedRows[index].rate) || 0;
      const taxRate = Number(updatedRows[index].taxRate) || 0;

      const subtotal = qty * rate;
      const taxAmount = (subtotal * taxRate) / 100;
      const total = subtotal + taxAmount;

      updatedRows[index].taxAmount = taxAmount.toFixed(2);
      updatedRows[index].total = total.toFixed(2);
    }

    setRows(updatedRows);
  };

  // 🔹 Add Row
  const handleAddRow = () => {
    // Always allow adding new rows and fetch items
    fetchItems().then(() => {
      setRows([
        ...rows,
        { itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "", taxRate: "", taxAmount: "", total: "", description: "", isBomItem: false },
      ]);
      // toastSuccess('New item row added. Items refreshed from API.');
    }).catch(() => {
      // Add row even if API fails
      setRows([
        ...rows,
        { itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "", taxRate: "", taxAmount: "", total: "", description: "", isBomItem: false },
      ]);
      // toastError('New row added but failed to refresh items from API.');
    });
  };

  // 🔹 Remove Row
  const handleRemoveRow = (index) => {
    const row = rows[index];
    if (row.isBomItem) {
      toastError('Cannot remove BOM items. Please clear BOM selection to modify items.');
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  // Commercial offer handlers
  const addParticularRow = () => {
    setParticulars([...particulars, { description: "", amount: "" }]);
  };

  const updateParticular = (index, field, value) => {
    const updated = [...particulars];
    updated[index][field] = value;
    setParticulars(updated);
  };

  const removeParticular = (index) => {
    setParticulars(particulars.filter((_, i) => i !== index));
  };

  const addSubsidyRow = () => {
    setSubsidies([...subsidies, { description: "", amount: "" }]);
  };

  const updateSubsidy = (index, field, value) => {
    const updated = [...subsidies];
    updated[index][field] = value;
    setSubsidies(updated);
  };

  const removeSubsidy = (index) => {
    setSubsidies(subsidies.filter((_, i) => i !== index));
  };

  const addPaymentTermRow = () => {
    setPaymentTerms([...paymentTerms, { description: "", amount: "", percentage: "" }]);
  };

  const updatePaymentTerm = (index, field, value) => {
    const updated = [...paymentTerms];

    // Validate percentage to not exceed 100% total
    if (field === 'percentage') {
      const currentTotal = paymentTerms.reduce((sum, pt, i) => {
        if (i === index) return sum; // Skip current row
        return sum + (Number(pt.percentage) || 0);
      }, 0);

      const newPercentage = Number(value) || 0;
      const newTotal = currentTotal + newPercentage;

      // Prevent total from exceeding 100%
      if (newTotal > 100) {
        const maxAllowed = 100 - currentTotal;
        toastError(`Cannot exceed 100% total. Maximum allowed: ${maxAllowed}%`);
        return; // Don't update if it would exceed 100%
      }
    }

    updated[index][field] = value;

    // Auto-calculate amount when percentage changes
    if (field === 'percentage') {
      const afterSubsidyCost = calculateAfterSubsidyCost();
      const percentage = Number(value) || 0;
      const calculatedAmount = (afterSubsidyCost * percentage) / 100;
      updated[index].amount = calculatedAmount.toFixed(2);
    }

    setPaymentTerms(updated);
  };

  const removePaymentTerm = (index) => {
    setPaymentTerms(paymentTerms.filter((_, i) => i !== index));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Get business ID from localStorage
      const currentBusinessId = localStorage.getItem('businessId');
      if (!currentBusinessId) {
        toastError('Business ID not found. Please login again.');
        return;
      }

      // Prepare items data
      const itemsData = rows
        .filter(row => row.itemName && row.variant && row.quantity)
        .map(row => ({
          item: row.itemName,
          variant: row.variant,
          qty: Number(row.quantity),
          rate: Number(row.rate)
        }));

      // Prepare commercial offer data with proper nested structure
      const particularsTotal = particulars.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      const subsidiesTotal = subsidies.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
      const paymentTermsTotal = paymentTerms.reduce((sum, pt) => sum + (Number(pt.amount) || 0), 0);

      const commercialOfferData = {
        total: particularsTotal, // Main total for the commercial offer
        particulars: [{
          items: particulars.filter(p => p.description && p.amount).map(p => ({
            description: p.description,
            amount: Number(p.amount)
          })),
          total: particularsTotal
        }],
        subsidies: [{
          items: subsidies.filter(s => s.description && s.amount).map(s => ({
            description: s.description,
            amount: Number(s.amount)
          })),
          total: subsidiesTotal
        }],
        paymentTerms: [{
          items: paymentTerms.filter(pt => pt.description && pt.amount).map(pt => ({
            description: pt.description,
            amount: Number(pt.amount),
            percentage: Number(pt.percentage) || 0
          })),
          total: paymentTermsTotal
        }]
      };

      // Prepare request data
      const requestData = {
        title,
        business: currentBusinessId,
        bom: bomId,
        lead: leadId,
        projectType: projectTypeId,
        note,
        quotationDate: quotationDate ? new Date(quotationDate).toISOString() : null,
        validDate: validDate ? new Date(validDate).toISOString() : null,
        showWarranty,
        warranty,
        showplantconfiguraton: showPlantConfiguration,
        plantconfiguraton: plantConfiguration,
        showScope,
        scope,
        showCommercialTermsAndConditions,
        commercialTermsAndConditions,
        showResidentialTermsAndConditions,
        residentialTermsAndConditions,
        showPrivacyPolicy,
        privacyPolicy,
        items: itemsData,
        commercialOffer: commercialOfferData,
        // Include calculation values
        calculations: {
          discount,
          margin,
          adjustment,
          gstRate,
          meterCharge,
          subsidyPercentage
        },
        // Also include as individual fields for backward compatibility
        discount,
        margin,
        adjustment,
        gstRate,
        meterCharge,
        subsidyPercentage
      };

      let response;
      if (isEdit) {
        response = await axiosInstance.put(`/api/quatation/${id}`, requestData);
      } else {
        response = await axiosInstance.post('/api/quatation', requestData);
      }

      if (response.data.Status === 1) {
        // Save quotation settings after successful quotation save
        await saveQuotationSettings();

        toastSuccess(response.data.Message || (isEdit ? "Quotation updated successfully!" : "Quotation created successfully!"));
        navigate('/user/quotation'); // Adjust navigation path as needed
      } else {
        toastError(response.data.Message || 'Failed to save quotation');
      }

    } catch (error) {
      console.error('Error saving quotation:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Error saving quotation. Please try again.';
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Save quotation then download PDF and redirect to index
  const handleSaveAndDownload = async () => {
    try {
      setLoading(true);

      const currentBusinessId = localStorage.getItem('businessId');
      if (!currentBusinessId) {
        toastError('Business ID not found. Please login again.');
        return;
      }

      const itemsData = rows
        .filter(row => row.itemName && row.variant && row.quantity)
        .map(row => ({
          item: row.itemName,
          variant: row.variant,
          qty: Number(row.quantity),
          rate: Number(row.rate)
        }));

      const particularsTotal = particulars.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      const subsidiesTotal = subsidies.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
      const paymentTermsTotal = paymentTerms.reduce((sum, pt) => sum + (Number(pt.amount) || 0), 0);

      const commercialOfferData = {
        total: particularsTotal,
        particulars: [{
          items: particulars.filter(p => p.description && p.amount).map(p => ({ description: p.description, amount: Number(p.amount) })),
          total: particularsTotal
        }],
        subsidies: [{
          items: subsidies.filter(s => s.description && s.amount).map(s => ({ description: s.description, amount: Number(s.amount) })),
          total: subsidiesTotal
        }],
        paymentTerms: [{
          items: paymentTerms.filter(pt => pt.description && pt.amount).map(pt => ({ description: pt.description, amount: Number(pt.amount), percentage: Number(pt.percentage) || 0 })),
          total: paymentTermsTotal
        }]
      };

      const requestData = {
        title,
        business: currentBusinessId,
        bom: bomId,
        lead: leadId,
        projectType: projectTypeId,
        note,
        quotationDate: quotationDate ? new Date(quotationDate).toISOString() : null,
        validDate: validDate ? new Date(validDate).toISOString() : null,
        showWarranty,
        warranty,
        showplantconfiguraton: showPlantConfiguration,
        plantconfiguraton: plantConfiguration,
        showScope,
        scope,
        showCommercialTermsAndConditions,
        commercialTermsAndConditions,
        showResidentialTermsAndConditions,
        residentialTermsAndConditions,
        showPrivacyPolicy,
        privacyPolicy,
        items: itemsData,
        commercialOffer: commercialOfferData,
        calculations: { discount, margin, adjustment, gstRate, meterCharge, subsidyPercentage },
        discount, margin, adjustment, gstRate, meterCharge, subsidyPercentage
      };

      let response;
      if (isEdit) {
        response = await axiosInstance.put(`/api/quatation/${id}`, requestData);
      } else {
        response = await axiosInstance.post('/api/quatation', requestData);
      }

      if (response.data.Status === 1) {
        await saveQuotationSettings();

        // Determine new quotation id from response
        const newId = response.data.quatation?._id || response.data.data?._id || response.data._id || response.data.id || null;
        toastSuccess(response.data.Message || (isEdit ? "Quotation updated successfully!" : "Quotation created successfully!"));

        if (newId) {
          try {
            const resp = await axiosInstance.get(`/api/quatation/${newId}/export`, { responseType: 'blob' });
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Quotation_${newId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toastSuccess('PDF downloaded successfully');
          } catch (err) {
            console.error('PDF download failed', err);
            toastError('Failed to download PDF');
          }
        }

        navigate('/user/quotation');
      } else {
        toastError(response.data.Message || 'Failed to save quotation');
      }

    } catch (error) {
      console.error('Error in save & download:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Error saving quotation. Please try again.';
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setLeadId("");
    setTitle("");
    setProjectTypeId("");
    setQuotationDate("");
    setValidDate("");
    setBomId("");
    setNote("");
    setShowPlantConfiguration(true);
    setPlantConfiguration("");
    setShowWarranty(true);
    setWarranty("");
    setShowScope(true);
    setScope("");
    setShowCommercialTermsAndConditions(true);
    setCommercialTermsAndConditions("");
    setShowResidentialTermsAndConditions(true);
    setResidentialTermsAndConditions("");
    setShowPrivacyPolicy(true);
    setPrivacyPolicy("");
    setRows([{ itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "", taxRate: "", taxAmount: "", total: "", description: "", isBomItem: false }]);
    setParticulars([{ description: "", amount: "" }]);
    setSubsidies([{ description: "", amount: "" }]);
    setPaymentTerms([{ description: "", amount: "", percentage: "" }]);
    setIsBomSelected(false);
  setBomMarginLocked(false);

    // Reset calculation states
    setDiscount(0);
    setMargin(0);
    setAdjustment(0);
    setGstRate(0);
    setMeterCharge(0);
    setSubsidyPercentage(0);
  };

  return (
    <div className="container-fluid py-4 px-3">
      <h2 className="fw-bold mb-4 text-dark">
        {isEdit ? "Edit Quotation" : "Add New Quotation"}
      </h2>


      <form
        className="item-form rounded-3 p-4"
        style={{
          background: "#fafbfc",
          border: "2.5px solid #c7dbed",
        }}
        onSubmit={handleSubmit}
      >
        {/* First Row */}
        <div className="row g-3 mb-2">
          <div className="col-md-3">
            <label className="form-label text-muted">Lead Name</label>
            <select
              className="form-select rounded-3"
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
            >
              <option value="">Select Lead</option>
              {leads.map((lead) => (
                <option key={lead._id} value={lead._id}>
                  {lead.name || lead.firstName + ' ' + lead.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label text-muted">Project Type</label>
            <select
              className="form-select rounded-3"
              value={projectTypeId}
              onChange={(e) => setProjectTypeId(e.target.value)}
            >
              <option value="">Select Project Type</option>
              {projectTypes.map((type) => (
                <option key={type._id} value={type._id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Add Button aligned to right */}
          <div className="col-md-6 d-flex justify-content-end align-items-center gap-1">
            {/* Text button */}
            <button
              type="button"
              className="btn btn-link text-primary text-decoration-none p-0 m-0"
              onClick={handleAddLead}
            >
              Add Lead
            </button>

            {/* Plus icon button */}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-2 d-flex align-items-center justify-content-center"
              onClick={handleAddLead}
            >
              +
            </button>
          </div>

        </div>

        {/* Second Row — BOM Only */}
        <div className="row g-3 mb-2">
          <div className="col-md-3">
            <label className="form-label text-muted">Quotation Title</label>
            <input
              type="text"
              className="form-control rounded-3"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter quotation title"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted">Quotation Date</label>
            <input
              type="date"
              className="form-control rounded-3"
              value={quotationDate}
              onChange={(e) => setQuotationDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label text-muted">Quotation Valid Date</label>
            <input
              type="date"
              className="form-control rounded-3"
              value={validDate}
              onChange={(e) => setValidDate(e.target.value)}
            />
          </div>
        </div>

        {/* ===== Plant Configuration Section ===== */}
        <div className="mb-3">
          {/* Switch Header (No Border) */}
          <div className="bg-light fw-semibold text-secondary py-2 px-3 rounded-3">
            <div className="form-check form-switch d-flex align-items-center mb-0 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="showPlantConfig"
                checked={showPlantConfiguration}
                onChange={(e) => setShowPlantConfiguration(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showPlantConfig">
                Show Plant Configuration
              </label>
            </div>
          </div>

          {showPlantConfiguration && (
            /* Outer Rounded Box with Header Line */
            <div className="border rounded-4 overflow-hidden mt-2">
              {/* Header Title */}
              <div
                className="border-bottom px-3 py-2 fw-bold text-center"
                style={{
                  backgroundColor: "#bbbfd2",
                  color: "#3F3F46",
                }}
              >
                Plant Configuration
              </div>

              <div
                className="d-flex justify-content-center align-items-center w-100"
                style={{
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    border: "1.5px solid #D5E3F0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    width: "70%",
                    margin: "0 auto",
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={plantConfiguration}
                    config={{
                      toolbar: {
                        items: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'undo',
                          'redo',
                        ],
                      },
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          {
                            minHeight: "560px",
                            padding: "12px",
                            backgroundColor: "#fff",
                          },
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setPlantConfiguration(data);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== Quotation Title Section ===== */}
        <div className="border rounded-4 overflow-hidden mt-3">
          {/* Header Title */}
          <div
            className="border-bottom px-3 py-2 fw-bold text-center"
            style={{
              backgroundColor: "#bbbfd2",
              color: "#3F3F46",
            }}
          >
            Select Bill Of Material (BOM)
          </div>

          <div className="d-flex justify-content-center align-items-center py-4 bg-white">
            <div className="col-12 px-5">
              <div className="d-flex align-items-center gap-3">
                <select
                  className="form-select form-select rounded-3 flex-grow-1"
                  value={bomId}
                  onChange={(e) => {
                    setBomId(e.target.value);
                    if (e.target.value) {
                      fetchBomItems(e.target.value);
                    } else {
                      // Clear items when no BOM selected
                      setRows([{ itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "", taxRate: "", taxAmount: "", total: "", description: "", isBomItem: false }]);
                      setIsBomSelected(false);
                        setBomMarginLocked(false);
                        setMargin(0);
                    }
                  }}
                >
                  <option value="">Select Bill Of Material</option>
                  {boms.map((bom) => (
                    <option key={bom._id} value={bom._id}>
                      {bom.code} - {bom.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

        </div>



        {/* ===== Material Details Section ===== */}
        <div className="card border-0 rounded-4 shadow-sm mt-4 bg-white">
          <div className="card-body p-4">
            <div className="table-responsive">
              <table
                className="table align-middle text-center mb-0"
                style={{
                  borderCollapse: "separate",
                  borderSpacing: "0 8px",
                  width: "100%",
                }}
              >
                <thead
                  className="border-bottom px-3 py-2 fw-bold text-center"
                  style={{
                    backgroundColor: "#bbbfd2",
                    color: "#3F3F46",
                  }}
                >
                  <tr>
                    <th style={{ borderTopLeftRadius: "8px", width: "60px" }}>Sr. No</th>
                    <th>Item</th>
                    <th>Variant</th>
                    <th>Company Name</th>
                    <th>Description</th>
                    <th>Unit</th>
                    <th>Qty</th>
                    <th>Rate (₹)</th>
                    <th>Tax Rate (%)</th>
                    <th>Tax Amount (₹)</th>
                    <th>Total (₹)</th>
                    <th style={{ width: "50px", borderTopRightRadius: "8px" }}></th>
                  </tr>
                </thead>


                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`shadow-sm ${row.isBomItem ? 'bg-info bg-opacity-10' : 'bg-white'}`}
                      style={{
                        borderRadius: "10px",
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                        border: row.isBomItem ? "1px solid #0dcaf0" : "none"
                      }}
                    >
                      <td className="fw-semibold">{idx + 1}</td>

                      <td>
                        {row.isBomItem ? (
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3"
                            value={
                              (items.find(i => i._id === row.itemName)?.name) || row.itemName || ""
                            }
                            readOnly
                          />
                        ) : (
                          <select
                            className="form-select form-select-sm rounded-3"
                            value={row.itemName}
                            onChange={(e) =>
                              handleRowChange(idx, "itemName", e.target.value)
                            }
                          >
                            <option value="">Select</option>
                            {items.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      <td>
                        {row.isBomItem ? (
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3"
                            value={
                              (items.find(i => i._id === row.itemName)
                                ?.variants?.find(v => v._id === row.variant)
                                ?.name) || row.variant || ""
                            }
                            readOnly
                          />
                        ) : (
                          <select
                            className="form-select form-select-sm rounded-3"
                            value={row.variant}
                            onChange={(e) =>
                              handleRowChange(idx, "variant", e.target.value)
                            }
                          >
                            <option value="">Select</option>
                            {getVariantsForItem(row.itemName).map((variant) => (
                              <option key={variant._id} value={variant._id}>
                                {variant.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm rounded-3"
                          placeholder="Company"
                          value={row.companyName}
                          onChange={(e) =>
                            handleRowChange(idx, "companyName", e.target.value)
                          }
                          readOnly={row.isBomItem}
                        />
                      </td>

                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm rounded-3"
                          placeholder="Description"
                          value={row.description}
                          onChange={(e) =>
                            handleRowChange(idx, "description", e.target.value)
                          }
                          readOnly={row.isBomItem}
                        />
                      </td>

                      <td>
                        <select
                          className="form-select form-select-sm rounded-3"
                          value={row.unit}
                          onChange={(e) => handleRowChange(idx, "unit", e.target.value)}
                          disabled={row.isBomItem}
                        >
                          <option value="">Unit</option>
                          {getAllUnits().map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm rounded-3 text-center"
                          placeholder="Qty"
                          value={row.quantity}
                          onChange={(e) =>
                            handleRowChange(idx, "quantity", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        {canViewRate ? (
                          <input
                            type="number"
                            className="form-control form-control-sm rounded-3 text-center"
                            placeholder="Rate"
                            value={row.rate}
                            onChange={(e) => {
                              // If user cannot edit Rate, prevent change
                              if (!canEditRate) return;
                              handleRowChange(idx, "rate", e.target.value);
                            }}
                            readOnly={!canEditRate}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3 text-center bg-light"
                            value={"-"}
                            readOnly
                            title="No permission to view rate"
                          />
                        )}
                      </td>

                      <td>
                        {canViewTaxRate ? (
                          <input
                            type="number"
                            className="form-control form-control-sm rounded-3 text-center"
                            placeholder="Tax %"
                            value={row.taxRate}
                            onChange={(e) => {
                              if (!canEditTaxRate || row.isBomItem) return;
                              handleRowChange(idx, "taxRate", e.target.value);
                            }}
                            readOnly={row.isBomItem || !canEditTaxRate}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3 text-center bg-light"
                            value={"-"}
                            readOnly
                            title="No permission to view tax rate"
                          />
                        )}
                      </td>

                      <td>
                        {canViewTaxAmount ? (
                          <input
                            type="number"
                            className="form-control form-control-sm rounded-3 text-center"
                            placeholder="Tax Amt"
                            value={row.taxAmount}
                            onChange={(e) =>
                              handleRowChange(idx, "taxAmount", e.target.value)
                            }
                            readOnly
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3 text-center bg-light"
                            value={"-"}
                            readOnly
                            title="No permission to view tax amount"
                          />
                        )}
                      </td>

                      <td>
                        {canViewTotal ? (
                          <input
                            type="number"
                            className="form-control form-control-sm rounded-3 text-center bg-light"
                            placeholder="Total"
                            value={row.total}
                            readOnly
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3 text-center bg-light"
                            value={"-"}
                            readOnly
                            title="No permission to view total"
                          />
                        )}
                      </td>

                      <td className="text-center">
                        <button
                          type="button"
                          className={`btn btn-sm d-flex justify-content-center align-items-center fw-bold p-0 ${row.isBomItem ? 'btn-outline-secondary' : 'btn-outline-danger'}`}
                          style={{
                            width: "26px",
                            height: "26px",
                            fontSize: "16px",
                            lineHeight: "1",
                            cursor: row.isBomItem ? "not-allowed" : "pointer",
                            opacity: row.isBomItem ? 0.5 : 1
                          }}
                          onClick={() => handleRemoveRow(idx)}
                          disabled={row.isBomItem}
                          title={row.isBomItem ? "Cannot remove BOM items" : "Remove this item"}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* + Add Item Button */}
            <div className="text-start mt-3">
              <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center justify-content-center fw-semibold"
                style={{
                  backgroundColor: "#E9EAF2",
                  borderColor: "#bbbfd2",
                  color: "#3F3F46",
                  fontSize: "14px",
                  padding: "8px 16px"
                }}
                onClick={handleAddRow}
                title="Add new item and refresh data from API"
              >
                + Add Item
              </button>
            </div>

            {/* Notes Field */}
            <div className="mt-3">
              <label
                className="form-label fw-semibold"
                style={{ color: "#3F3F46" }}
              >
                Notes...
              </label>
              <textarea
                className="form-control rounded-3"
                rows="4"
                style={{ width: "30%", fontSize: "14px" }}
                placeholder="Enter your notes here..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              ></textarea>
            </div>

            {/* Totals Section */}
            <div className="d-flex flex-column align-items-end mt-4 fs-5">

              {/* Sub Total */}
              <div className="mb-2">
                Sub Total: <span className="fw-semibold">₹{calculateSubtotal().toFixed(2)}</span>
              </div>

              {/* Discount */}
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <label className="form-label mb-0 fw-semibold text-end" style={{ width: "160px" }}>
                  Discount ₹:
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm w-50 rounded-2"
                  placeholder="Enter Discount"
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                />
              </div>

              {/* Margin Percentage */}
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <label className="form-label mb-0 fw-semibold text-end" style={{ width: "160px" }}>
                  Margin %:
                </label>
                  <input
                    type="number"
                    className="form-control form-control-sm w-50 rounded-2"
                    placeholder="Enter Margin %"
                    value={margin}
                    onChange={(e) => setMargin(Number(e.target.value) || 0)}
                    disabled={bomMarginLocked}
                  />
                  {bomMarginLocked && (
                    <small className="text-muted ms-2"></small>
                  )}
              </div>

              {/* Margin Amount */}
              <div className="mb-2">
                Margin Amount: <span className="fw-semibold">₹{calculateMarginAmount().toFixed(2)}</span>
              </div>

              {/* GST Rate */}
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <label className="form-label mb-0 fw-semibold text-end" style={{ width: "160px" }}>
                  GST %:
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm w-50 rounded-2"
                  placeholder="GST Rate"
                  value={gstRate}
                  onChange={(e) => setGstRate(Number(e.target.value) || 0)}
                />
              </div>

              {/* GST Amount */}
              <div className="mb-2">
                GST Amount: <span className="fw-semibold">₹{calculateGstAmount().toFixed(2)}</span>
              </div>

              {/* Adjustments */}
              <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
                <label className="form-label mb-0 fw-semibold text-end" style={{ width: "160px" }}>
                  Adjustments ₹:
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm w-50 rounded-2"
                  placeholder="Enter Adjustments"
                  value={adjustment}
                  onChange={(e) => setAdjustment(Number(e.target.value) || 0)}
                />
              </div>

              {/* Grand Total */}
              <div className="fw-bold fs-4 text-primary mt-2 mb-3">
                Grand Total: <span>₹{calculateSimpleGrandTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>


        <div className="mb-4">
          {/* ===== Outer Rounded Border Box ===== */}
          <div className="border rounded-4 overflow-hidden">
            {/* ===== Header Title ===== */}
            <div className="border-bottom text-center fw-bold py-2" style={{ backgroundColor: "#bbbfd2", color: "#3F3F46" }}>
              Commercial Offer
            </div>


            <div
              className="border rounded-4 overflow-hidden mx-auto mb-4 mt-4"
              style={{
                maxWidth: "1000px",
                borderColor: "#D6D8E3",
                backgroundColor: "#FFFFFF",
              }}
            >
              {/* ===== Header Title ===== */}
              <div
                className="border-bottom text-center fw-bold py-2"
                style={{
                  backgroundColor: "#C6CBDA",
                  color: "#3F3F46",
                }}
              >
                Particular
              </div>

              {/* ===== Table Section ===== */}
              <div className="p-3 pt-2">
                <div
                  style={{
                    border: "1px solid #D6D8E3",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <Table
                    bordered
                    responsive
                    className="mb-0 text-center"
                    style={{
                      borderCollapse: "collapse",
                      marginBottom: "0",
                    }}
                  >
                    <thead
                      className="table-light align-middle"
                      style={{
                        border: "1px solid #D6D8E3",
                        backgroundColor: "#F7F8FB",
                      }}
                    >
                      <tr>
                        <th style={{ width: "60px", border: "1px solid #D6D8E3" }}>Sr No.</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Description</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Amount (₹)</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {particulars.map((particular, index) => (
                        <tr key={index}>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control type="text" value={index + 1} readOnly className="text-center" />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control
                              type="text"
                              placeholder="Description"
                              value={particular.description}
                              onChange={(e) => updateParticular(index, 'description', e.target.value)}
                            />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control
                              type="number"
                              placeholder="Amount"
                              value={particular.amount}
                              onChange={(e) => updateParticular(index, 'amount', e.target.value)}
                            />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            {particulars.length === index + 1 ? (
                              <Button variant="outline-primary" className="fw-bold" onClick={addParticularRow}>
                                +
                              </Button>
                            ) : (
                              <Button variant="outline-danger" className="fw-bold" onClick={() => removeParticular(index)}>
                                ×
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}

                      <tr
                        className="fw-semibold text-secondary text-end align-middle"
                        style={{ borderTop: "2px solid #D6D8E3" }}
                      >

                        <td colSpan={2} style={{ border: "1px solid #D6D8E3" }}>
                          Net Payable Amount
                        </td>
                        <td colSpan={2} style={{ border: "1px solid #D6D8E3" }}>
                          <Form.Control
                            type="number"
                            value={(calculateSimpleGrandTotal() + particulars.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)).toFixed(2)}
                            readOnly
                            className="bg-light fw-semibold text-end"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>



            {/* ===== Subsidy Section ===== */}
            <div
              className="border rounded-4 overflow-hidden mx-auto mb-4 mt-4"
              style={{
                maxWidth: "1000px",
                borderColor: "#D6D8E3",
                backgroundColor: "#FFFFFF",
              }}
            >
              {/* ===== Header Title ===== */}
              <div
                className="border-bottom text-center fw-bold py-2"
                style={{
                  backgroundColor: "#C6CBDA",
                  color: "#3F3F46",
                }}
              >
                Subsidy Calculation As per Govt. Subsidy Structure
              </div>

              {/* ===== Table Section ===== */}
              <div className="p-3 pt-2">
                <div
                  style={{
                    border: "1px solid #D6D8E3",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <Table
                    bordered
                    responsive
                    className="mb-0 text-center"
                    style={{
                      borderCollapse: "collapse",
                      marginBottom: "0",
                    }}
                  >
                    <thead
                      className="table-light align-middle"
                      style={{
                        border: "1px solid #D6D8E3",
                        backgroundColor: "#F7F8FB",
                      }}
                    >
                      <tr>
                        <th style={{ width: "60px", border: "1px solid #D6D8E3" }}>Sr No.</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Description</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Amount (₹)</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subsidies.map((subsidy, index) => (
                        <tr key={index}>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control type="text" value={index + 1} readOnly className="text-center" />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control
                              type="text"
                              placeholder="Description"
                              value={subsidy.description}
                              onChange={(e) => updateSubsidy(index, 'description', e.target.value)}
                            />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control
                              type="number"
                              placeholder="Amount"
                              value={subsidy.amount}
                              onChange={(e) => updateSubsidy(index, 'amount', e.target.value)}
                            />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            {subsidies.length === index + 1 ? (
                              <Button variant="outline-primary" className="fw-bold" onClick={addSubsidyRow}>
                                +
                              </Button>
                            ) : (
                              <Button variant="outline-danger" className="fw-bold" onClick={() => removeSubsidy(index)}>
                                ×
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}

                      <tr
                        className="fw-semibold text-secondary text-end align-middle"
                        style={{ borderTop: "2px solid #D6D8E3" }}
                      >
                        <td colSpan={2} style={{ border: "1px solid #D6D8E3" }}>
                          Subsidy Amount (₹)
                        </td>
                        <td colSpan={2} style={{ border: "1px solid #D6D8E3" }}>
                          <Form.Control
                            type="number"
                            value={subsidies.reduce((sum, s) => sum + (Number(s.amount) || 0), 0).toFixed(2)}
                            readOnly
                            className="bg-light fw-semibold text-end"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* ===== After Subsidy Cost ===== */}
                <div className="d-flex justify-content-end align-items-center fw-medium text-secondary mt-3">
                  After Subsidy Project Cost:&nbsp;
                  <Form.Control
                    type="number"
                    value={(
                      (calculateSimpleGrandTotal() + particulars.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)) -
                      subsidies.reduce((sum, s) => sum + (Number(s.amount) || 0), 0)
                    ).toFixed(2)}
                    readOnly
                    className="bg-light fw-semibold text-end"
                    style={{ width: "120px" }}
                  />
                </div>
              </div>
            </div>


            {/* ===== Payment Terms Section ===== */}
            <div
              className="border rounded-4 overflow-hidden mx-auto mb-4 mt-4"
              style={{
                maxWidth: "1000px",
                borderColor: "#D6D8E3",
                backgroundColor: "#FFFFFF",
              }}
            >
              {/* ===== Header Title ===== */}
              <div
                className="border-bottom text-center fw-bold py-2"
                style={{
                  backgroundColor: "#C6CBDA",
                  color: "#3F3F46",
                }}
              >
                Payment Terms
              </div>

              {/* ===== Table Section ===== */}
              <div className="p-3 pt-2">
                <div
                  style={{
                    border: "1px solid #D6D8E3",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <Table
                    bordered
                    responsive
                    className="mb-0 text-center"
                    style={{
                      borderCollapse: "collapse",
                      marginBottom: "0",
                    }}
                  >
                    <thead
                      className="table-light align-middle"
                      style={{
                        border: "1px solid #D6D8E3",
                        backgroundColor: "#F7F8FB",
                      }}
                    >
                      <tr>
                        <th style={{ border: "1px solid #D6D8E3" }}>Percentage (%)</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Description</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Amount (₹)</th>
                        <th style={{ border: "1px solid #D6D8E3" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentTerms.map((paymentTerm, index) => (
                        <tr key={index}>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <InputGroup>
                              <InputGroup.Text>%</InputGroup.Text>
                              <Form.Control
                                type="number"
                                min={0}
                                max={100}
                                placeholder="0"
                                value={paymentTerm.percentage}
                                onChange={(e) => updatePaymentTerm(index, 'percentage', e.target.value)}
                              />
                            </InputGroup>
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control
                              type="text"
                              placeholder="Description"
                              value={paymentTerm.description}
                              onChange={(e) => updatePaymentTerm(index, 'description', e.target.value)}
                            />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            <Form.Control
                              type="number"
                              placeholder="0.00"
                              value={paymentTerm.amount}
                              readOnly
                              className="bg-light"
                            />
                          </td>
                          <td style={{ border: "1px solid #D6D8E3" }}>
                            {paymentTerms.length === index + 1 ? (
                              <Button variant="outline-primary" className="fw-bold" onClick={addPaymentTermRow}>
                                +
                              </Button>
                            ) : (
                              <Button variant="outline-danger" className="fw-bold" onClick={() => removePaymentTerm(index)}>
                                ×
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}

                      {/* Total Row */}
                      <tr
                        className="fw-semibold text-secondary text-end align-middle"
                        style={{ borderTop: "2px solid #D6D8E3", backgroundColor: "#F7F8FB" }}
                      >
                        <td style={{ border: "1px solid #D6D8E3" }}>
                          <Form.Control
                            type="text"
                            value={`${paymentTerms.reduce((sum, pt) => sum + (Number(pt.percentage) || 0), 0).toFixed(1)}%`}
                            readOnly
                            className="bg-light fw-semibold text-center"
                            style={{
                              color: (() => {
                                const total = paymentTerms.reduce((sum, pt) => sum + (Number(pt.percentage) || 0), 0);
                                if (total === 100) return '#28a745'; // Green for exactly 100%
                                if (total < 100) return '#ffc107'; // Yellow for less than 100%
                                return '#dc3545'; // Red for over 100%
                              })()
                            }}
                          />
                        </td>
                        <td style={{ border: "1px solid #D6D8E3" }}>
                          <Form.Control
                            type="text"
                            value="Total Payment Terms"
                            readOnly
                            className="bg-light fw-semibold text-center"
                          />
                        </td>
                        <td style={{ border: "1px solid #D6D8E3" }}>
                          <Form.Control
                            type="text"
                            value={`₹${paymentTerms.reduce((sum, pt) => sum + (Number(pt.amount) || 0), 0).toFixed(2)}`}
                            readOnly
                            className="bg-light fw-semibold text-center"
                          />
                        </td>
                        <td style={{ border: "1px solid #D6D8E3" }}>
                          <span className={`badge ${(() => {
                            const total = paymentTerms.reduce((sum, pt) => sum + (Number(pt.percentage) || 0), 0);
                            if (total === 100) return 'bg-success';
                            if (total < 100) return 'bg-warning';
                            return 'bg-danger';
                          })()}`}>
                            {(() => {
                              const total = paymentTerms.reduce((sum, pt) => sum + (Number(pt.percentage) || 0), 0);
                              if (total === 100) return '✓ Complete (100%)';
                              if (total < 100) return `⚠ Partial (${total.toFixed(1)}%)`;
                              return `✗ Exceeded (${total.toFixed(1)}%)`;
                            })()}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* Base Amount Info */}
                <div className="mt-3 p-2 bg-light rounded">
                  <small className="text-muted">
                    <strong>Base Amount for Percentage Calculation:</strong> ₹{calculateAfterSubsidyCost().toFixed(2)} (After Subsidy Cost)
                    <br />
                    <em>Note: Total percentage can be less than or equal to 100%. Exceeding 100% is not allowed.</em>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>


        <div className="mb-3">
          {/* Switch Header (No Border) */}
          <div className="bg-light fw-semibold text-secondary py-2 px-3 rounded-3">
            <div className="form-check form-switch d-flex align-items-center mb-0 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="showWarranty"
                checked={showWarranty}
                onChange={(e) => setShowWarranty(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showWarranty">
                Show Warranty
              </label>
            </div>
          </div>

          {showWarranty && (
            /* Outer Rounded Box with Header Line */
            <div className="border rounded-4 overflow-hidden mt-2">
              {/* Header Title */}
              <div
                className="border-bottom px-3 py-2 fw-bold text-center"
                style={{
                  backgroundColor: "#bbbfd2",
                  color: "#3F3F46",
                }}
              >
                Warranty
              </div>

              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    border: "1.5px solid #D5E3F0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    width: "70%",
                    margin: "0 auto",
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={warranty}
                    config={{
                      toolbar: {
                        items: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'undo',
                          'redo',
                        ],
                      },
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          {
                            minHeight: "560px",
                            padding: "12px",
                            backgroundColor: "#fff",
                          },
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setWarranty(data);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>


        <div className="mb-3">
          {/* Switch Header (No Border) */}
          <div className="bg-light fw-semibold text-secondary py-2 px-3 rounded-3">
            <div className="form-check form-switch d-flex align-items-center mb-0 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="showScope"
                checked={showScope}
                onChange={(e) => setShowScope(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showScope">
                Show Scope
              </label>
            </div>
          </div>

          {showScope && (
            /* Outer Rounded Box with Header Line */
            <div className="border rounded-4 overflow-hidden mt-2">
              {/* Header Title */}
              <div
                className="border-bottom px-3 py-2 fw-bold text-center"
                style={{
                  backgroundColor: "#bbbfd2",
                  color: "#3F3F46",
                }}
              >
                Scope of Company
              </div>

              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    border: "1.5px solid #D5E3F0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    width: "70%",
                    margin: "0 auto",
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={scope}
                    config={{
                      toolbar: {
                        items: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'undo',
                          'redo',
                        ],
                      },
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          {
                            minHeight: "560px",
                            padding: "12px",
                            backgroundColor: "#fff",
                          },
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setScope(data);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>


        <div className="mb-3">
          {/* Switch Header (No Border) */}
          <div className="bg-light fw-semibold text-secondary py-2 px-3 rounded-3">
            <div className="form-check form-switch d-flex align-items-center mb-0 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="showCommercialTerms"
                checked={showCommercialTermsAndConditions}
                onChange={(e) => setShowCommercialTermsAndConditions(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showCommercialTerms">
                Show Commercial Terms & Conditions
              </label>
            </div>
          </div>

          {showCommercialTermsAndConditions && (
            /* Outer Rounded Box with Header Line */
            <div className="border rounded-4 overflow-hidden mt-2">
              {/* Header Title */}
              <div
                className="border-bottom px-3 py-2 fw-bold text-center"
                style={{
                  backgroundColor: "#bbbfd2",
                  color: "#3F3F46",
                }}
              >
                Commercial Terms & Conditions
              </div>

              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    border: "1.5px solid #D5E3F0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    width: "70%",
                    margin: "0 auto",
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={commercialTermsAndConditions}
                    config={{
                      toolbar: {
                        items: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'undo',
                          'redo',
                        ],
                      },
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          {
                            minHeight: "560px",
                            padding: "12px",
                            backgroundColor: "#fff",
                          },
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setCommercialTermsAndConditions(data);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>


        <div className="mb-3">
          {/* Switch Header (No Border) */}
          <div className="bg-light fw-semibold text-secondary py-2 px-3 rounded-3">
            <div className="form-check form-switch d-flex align-items-center mb-0 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="showResidentialTerms"
                checked={showResidentialTermsAndConditions}
                onChange={(e) => setShowResidentialTermsAndConditions(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showResidentialTerms">
                Show Residential Terms & Conditions
              </label>
            </div>
          </div>

          {showResidentialTermsAndConditions && (
            /* Outer Rounded Box with Header Line */
            <div className="border rounded-4 overflow-hidden mt-2">
              {/* Header Title */}
              <div
                className="border-bottom px-3 py-2 fw-bold text-center"
                style={{
                  backgroundColor: "#bbbfd2",
                  color: "#3F3F46",
                }}
              >
                Residential Terms & Conditions
              </div>

              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    border: "1.5px solid #D5E3F0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    width: "70%",
                    margin: "0 auto",
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={residentialTermsAndConditions}
                    config={{
                      toolbar: {
                        items: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'undo',
                          'redo',
                        ],
                      },
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          {
                            minHeight: "560px",
                            padding: "12px",
                            backgroundColor: "#fff",
                          },
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setResidentialTermsAndConditions(data);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>


        <div className="mb-3">
          {/* Switch Header (No Border) */}
          <div className="bg-light fw-semibold text-secondary py-2 px-3 rounded-3">
            <div className="form-check form-switch d-flex align-items-center mb-0 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="showPrivacyPolicy"
                checked={showPrivacyPolicy}
                onChange={(e) => setShowPrivacyPolicy(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showPrivacyPolicy">
                Show Privacy Policy
              </label>
            </div>
          </div>

          {showPrivacyPolicy && (
            /* Outer Rounded Box with Header Line */
            <div className="border rounded-4 overflow-hidden mt-2">
              {/* Header Title */}
              <div
                className="border-bottom px-3 py-2 fw-bold text-center"
                style={{
                  backgroundColor: "#bbbfd2",
                  color: "#3F3F46",
                }}
              >
                Privacy Policy
              </div>

              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    border: "1.5px solid #D5E3F0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    width: "70%",
                    margin: "0 auto",
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={privacyPolicy}
                    config={{
                      toolbar: {
                        items: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'undo',
                          'redo',
                        ],
                      },
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          {
                            minHeight: "560px",
                            padding: "12px",
                            backgroundColor: "#fff",
                          },
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setPrivacyPolicy(data);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Buttons Section */}
        <div className="d-flex justify-content-end gap-2 mt-5">

          {/* Cancel Button */}
          <button
            type="button"
            className="btn btn-light btn-lg rounded-3 d-flex flex-column align-items-center justify-content-center"
            onClick={() => navigate('/quotations')}
            disabled={loading}
          >
            <i className="bi bi-x-circle fs-4 mb-1"></i>
            Cancel
          </button>

          {/* Save Button */}
          <button
            type="submit"
            className="btn btn-primary btn-lg rounded-3 d-flex flex-column align-items-center justify-content-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm mb-1" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                {isEdit ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>
                <i className="bi bi-save fs-4 mb-1"></i>
                {isEdit ? 'Update' : 'Save'}
              </>
            )}
          </button>

          <button
            type="button"
            className="btn btn-outline-primary btn-lg rounded-3 d-flex align-items-center justify-content-center gap-2"
            disabled={loading}
            onClick={handleSaveAndDownload}
          >
            <i className="bi bi-download fs-5"></i>
            Save & Download
            <i className="bi bi-caret-down-fill fs-6"></i>
          </button>
        </div>

      </form>
    </div>
  );
}
